import { uniqBy } from 'lodash';
import { apolloClient } from '~/apollo-client';
import { LDClient, LDFlags } from '~/providers';
import {
  ContractTag,
  Flex_FetchVnmTermsQuery,
  Flex_FetchVnmTermsQueryVariables,
  SubscriberRequirementStatus,
} from '~/typings/graphql';
import { STATE_IDS, UTILITY_IDS } from '~/utils/constants';
import { determineProjectGroup } from '~/utils/project-groups';
import { fetchUsageRequestForm } from '../fetch-usage-request-form';
import { FETCH_TERMS } from './fetch-vnm-terms.queries';

const AGENCY_CONTRACT_ID = 30;
// States where we don't support using agency and as a result want to filter it out of general contracts.
const NO_AGENCY_STATES: number[] = [STATE_IDS.IL, STATE_IDS.MA];

export const fetchVnmTerms = async (
  params: FetchVnmTermsParams,
): Promise<FetchVnmTermsResponse> => {
  const { stateId, contractTags, utilityAccountId, funnelId } = params;

  const { data } = await apolloClient.query<
    Flex_FetchVnmTermsQuery,
    Flex_FetchVnmTermsQueryVariables
  >({
    query: FETCH_TERMS,
    variables: { stateId, tags: contractTags ?? [], utilityAccountId },
  });

  const agencyCommunitySolarContractType = 'vnm';
  const isOregon = STATE_IDS.OR === stateId;
  const termsAndPrivacyContractIds = [31, 43];
  const generalContracts = data.generalContractVersions.filter(contractVersion =>
    termsAndPrivacyContractIds.includes(contractVersion?.contractId),
  );
  // We may not require this oregon filtering and the same could be
  // achieved by adding OR to no agency states above. I don't want to mix this change
  // with perch changes , let's keep it as is for now.
  const stateGeneralContractVersions = data.generalContractVersions.filter(
    contractVersion =>
      !(isOregon && agencyCommunitySolarContractType === contractVersion?.contractType),
  );

  const requiredContractVersions: ContractVersionWithSignature[] =
    data.utilityAccount.subscriberContractRequirement.dependencies
      .filter(cr => cr.status === SubscriberRequirementStatus.Pending)
      .flatMap(cr => {
        if (cr.__typename === 'ContractSubscriberRequirement') {
          return [
            {
              displayName: cr.contract.displayName,
              contractId: cr.contract.contractId,
              contractType: cr.contract.contractType,
              tag: cr?.contract?.tag as ContractTag,
              ...cr.contract.currentVersion!,
            },
          ];
        } else {
          return [];
        }
      });

  const notAgencyOrAllowed = (c: ContractVersion) => {
    return c.contractId !== AGENCY_CONTRACT_ID || !NO_AGENCY_STATES.includes(stateId);
  };

  const docuSignContracts = requiredContractVersions.filter(c => c.signatureType === 'docusign');

  let usageRequestContracts: ContractVersion[] = [];

  const shouldUseAgreementForUsageRequest = LDClient.variation(LDFlags.ffEnableContractForm);

  // Fetch usage request form for MN state for all funnels
  // based on the community solar project group associated with Xcel Energy MN
  const shouldFetchUsageRequest = stateId === STATE_IDS.MN || stateId === STATE_IDS.VA;

  if (shouldFetchUsageRequest) {
    let communitySolarProjectGroupId: number | null = null;
    if (stateId === STATE_IDS.MN) {
      communitySolarProjectGroupId = determineProjectGroup(UTILITY_IDS['Xcel Energy MN'], funnelId);
    }
    const usageRequestData = await fetchUsageRequestForm({
      stateId,
      communitySolarProjectGroupId,
    });
    usageRequestContracts = usageRequestData.docuSignContracts;
  }

  const contracts: ContractVersion[] = unique([
    requiredContractVersions.filter(c => c.signatureType === 'agreement'),
    stateGeneralContractVersions,
    data.taggedContractVersions,
    // Add usage request as agreement if VA (always) or if LD flag is enabled (for MN)
    ...(stateId === STATE_IDS.VA || shouldUseAgreementForUsageRequest
      ? [usageRequestContracts]
      : []),
  ])
    .filter(contractVersion => !termsAndPrivacyContractIds.includes(contractVersion?.contractId))
    .filter(notAgencyOrAllowed);

  // Combine all docusign contracts:
  // - LMI form (if present)
  // - Usage request form (for MN + go-solar/isolar only) when LD flag is disabled
  // - Normal docusign contracts
  // - State specific handling for MA to avoid combining LMI with others
  const allDocuSignContracts = [
    ...(data.lmiForm && STATE_IDS.MA !== stateId ? [data.lmiForm] : []),
    // Add usage request as docusign only for MN if LD flag is disabled (VA always uses agreement)
    ...(stateId !== STATE_IDS.VA && !shouldUseAgreementForUsageRequest
      ? usageRequestContracts
      : []),
    ...docuSignContracts,
  ];

  return {
    // The LMI docusign is mutually exclusive with the "normal" DocuSign flow, so adding this check
    // so we don't inadvertently combine a docusign and LMI form into the same envelope.
    // With our current data there shouldn't be a situation where this happens. This assumption
    // has changed recently with MA , so adding this check here.
    // Usage request form is added to contracts for MN go-solar/isolar funnels as we do not have LMI form there.
    docuSignContracts:
      data.lmiForm && !shouldFetchUsageRequest && STATE_IDS.MA !== stateId
        ? [data.lmiForm]
        : allDocuSignContracts,
    generalContracts,
    showLmi: !!data.lmiForm,
    vnmSolarProjectContracts: contracts,
  };
};

// address overlap in contracts across state, project, and tagged categories
const unique = (contracts: ContractVersion[][]): ContractVersion[] =>
  uniqBy(contracts.flat(), 'id');

export type ContractVersion = {
  contractId: number;
  contractType: string;
  displayName: string;
  fileUrl: string;
  id: number;
  scrollRequired: boolean;
  tag?: ContractTag | null;
  contractVersionLanguageId?: number | null;
  signatureType?: string;
};

type ContractVersionWithSignature = ContractVersion & {
  signatureType: string;
};

export type FetchVnmTermsParams = {
  stateId: number;
  contractTags?: ContractTag[];
  utilityAccountId: number;
  funnelId?: string;
};

export interface FetchVnmTermsResponse {
  docuSignContracts: ContractVersion[];
  showLmi?: boolean;
  generalContracts: ContractVersion[];
  vnmSolarProjectContracts: ContractVersion[];
}
